import type { MouseEvent } from "react";
import { useTranslation } from "react-i18next";
import type { TocHeading } from "@/hooks/useTableOfContents";

interface PageTableOfContentsProps {
  headings: TocHeading[];
  activeId: string | null;
}

/**
 * Sticky table of contents shown in the right gutter on wide screens.
 * Renders nothing for short pages (fewer than 2 headings).
 */
export function PageTableOfContents({ headings, activeId }: PageTableOfContentsProps) {
  const { t } = useTranslation();

  if (headings.length < 2) return null;

  const handleClick = (e: MouseEvent<HTMLAnchorElement>, id: string) => {
    e.preventDefault();
    const el = document.getElementById(id);
    if (!el) return;
    el.scrollIntoView({ behavior: "smooth", block: "start" });
    history.replaceState(null, "", `#${id}`);
  };

  return (
    <nav aria-label={t("page.tableOfContents")} className="sticky top-24">
      <div className="mb-3 text-xs font-semibold uppercase tracking-[0.12em] text-muted-foreground">
        {t("page.tableOfContents")}
      </div>
      <ul className="border-l border-border">
        {headings.map((h) => {
          const active = h.id === activeId;
          return (
            <li key={h.id}>
              <a
                href={`#${h.id}`}
                onClick={(e) => handleClick(e, h.id)}
                className={[
                  "-ml-px block border-l-2 py-1 text-sm leading-snug transition-colors",
                  h.level === 3 ? "pl-7" : "pl-4",
                  active
                    ? "border-primary font-medium text-primary"
                    : "border-transparent text-muted-foreground hover:border-border hover:text-foreground",
                ].join(" ")}
              >
                {h.text}
              </a>
            </li>
          );
        })}
      </ul>
    </nav>
  );
}
